import React, { useCallback, useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'

import { Theme } from '@mui/material/styles'
import { makeStyles } from '@mui/styles'
import { Box, FormControlLabel, Grid, Paper } from '@mui/material/'

import KeyboardArrowLeftRounded from '@mui/icons-material/KeyboardArrowLeftRounded'
import ButtonComponent from '@professocial/professocialFormComponents/FormComponents/ButtonComponent'
import SwitchComponent from '@professocial/professocialFormComponents/FormComponents/SwitchComponent'
import Preloader from '@professocial/professocialFormComponents/FormComponents/Preloader'

import {
  DeferredPost,
  DeferredPostStatus,
  PostTemplate,
  PostTemplateSort,
  PostTemplatesSearchResult,
  SortingOrder,
  TopicsWithMetadataResponse,
  TopicWithMetadata,
} from '@3m5/sm_publish-frontend-api'
import { useDispatch, useSelector } from 'react-redux'
import { getAboTopics, getDeferredPosts, getTags, getTemplates } from '../../../../store/posting/posting.selector'
import {
  DeletePostTemplateRequest,
  FindPostTemplatesForCurrentUserLightweightRequest
} from '@3m5/sm_publish-frontend-api'

import {
  deletePostTemplateInitiated,
  findPostTemplatesForCurrentUserInitiated,
  listDeferredPostsInitiated,
  setPostingType,
} from '../../../../store/posting/posting.actions'
import DropDownComponent, {
  DropDownElement,
} from '@professocial/professocialFormComponents/FormComponents/DropDownComponent'
import { FilterType, PostingScreen, PostingType } from '../../../types/types'
import Pagination from '@professocial/professocialFormComponents/FormComponents/Pagination'
import ShowPostTemplate from '../ShowPostTemplate'
import { getUser, namedRequestData } from '../../../../store/common/common.selectors'
import DialogComponent from '@professocial/professocialFormComponents/FormComponents/DialogComponent'
import { PostingRootState } from '../../../../store/postingRoot.reducer'
import { ActionCall } from '@professocial/professocialTypes/types/commonTypes'
import { clearRequests } from '../../../../store/common/common.actions'
import NoDataComponent from '@professocial/professocialFormComponents/FormComponents/NoDataComponent'
import { UserSettings } from '@professocialApi/professocial-core-frontend-api/models/UserSettings'

export interface TemplateFilter {
  postStartTo: Date
  tags: DropDownElement[]
  topic: string | DropDownElement[]
  sort: PostTemplateSort
  onlyFailedPostings?: boolean
  limit: number
  offset: number
}

interface IProps {
  templateData?: PostTemplatesSearchResult
  user: any
  allowTemplateEdit?: boolean
  changePosting: (post: PostTemplate | undefined, deferredPost: DeferredPost | undefined) => void
  changePostingScreen?: (value: PostingScreen) => void
  showTemplateInitState?: boolean
}

const useStyles = makeStyles((theme: Theme) => ({
  paper: {
    width: '100%',
    padding: theme.spacing(4),
    marginBottom: '10px',
  },
  button: {
    fontWeight: theme.typography.fontWeightBold,
    fontSize: theme.typography.body1.fontSize,
    textTransform: 'initial',
    marginLeft: '20px',
  },
  noComments: {
    flex: '0 0 auto',
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center',
    height: '100%',
  },
}))

const Template: React.FC<IProps> = (props) => {
  const pageSize = 10
  const dispatch = useDispatch()
  const classes = useStyles()
  const { t } = useTranslation()

  const user: UserSettings | undefined = useSelector(getUser)

  const initialFilterOptions = [
    { key: FilterType.template, value: t('postingCommon:filterTemplate') },
    { key: FilterType.topic, value: t('postingCommon:filterTopic') },
  ]

  const [showTemplates, setShowTemplates] = useState<boolean>(props.showTemplateInitState ?? false)
  const [filterOptions, setFilterOptions] = useState<DropDownElement[]>(initialFilterOptions)
  const [filteredData, setFilteredData] = useState<(DeferredPost | PostTemplate)[]>()
  const [topics, setTopics] = useState<DropDownElement[]>([])
  const [categories, setCategories] = useState<DropDownElement[]>([])

  const [selectedCategory, setSelectedCategory] = useState<string | undefined>(undefined)
  const [selectedTopic, setSelectedTopic] = useState<string | undefined>(undefined)

  const [tags, setTags] = useState<DropDownElement[]>()
  const [page, setPage] = useState<number>(1)
  const [toast, setToast] = React.useState<any>()
  const [filter, setFilter] = useState<TemplateFilter>({
    postStartTo: new Date(),
    tags: [],
    topic: 'all',
    sort: PostTemplateSort.ByPostStart,
    onlyFailedPostings: false,
    limit: 10,
    offset: 0,
  })

  const sortingOptions: DropDownElement[] = [
    { key: PostTemplateSort.ByPostStart, value: t('postingCommon:sortNewFirst') },
    { key: PostTemplateSort.ByPopularity, value: t('postingCommon:sortPopularFirst') },
  ]

  const dispatchGetPostTemplates = useCallback((params: FindPostTemplatesForCurrentUserLightweightRequest) => dispatch(findPostTemplatesForCurrentUserInitiated(params)), [dispatch])
  const dispatchGetDeferredPosts = useCallback(() => dispatch(listDeferredPostsInitiated()), [dispatch])
  const dispatchDeletePostTemplate = useCallback((params: DeletePostTemplateRequest) => dispatch(deletePostTemplateInitiated(params)), [dispatch])

  const templateData: PostTemplatesSearchResult | undefined = useSelector(getTemplates)
  const storedCategories: TopicsWithMetadataResponse | undefined = useSelector(getAboTopics)
  const tagsData = useSelector(getTags)
  const deferredPostsData = useSelector(getDeferredPosts)

  const actionGetPostTemplates: ActionCall | undefined = useSelector((state: PostingRootState) => namedRequestData(state, findPostTemplatesForCurrentUserInitiated.type))
  const actionGetDeferredPosts: ActionCall | undefined = useSelector((state: PostingRootState) => namedRequestData(state, listDeferredPostsInitiated.type))
  const actionOnTemplateDelete: ActionCall | undefined = useSelector((state: PostingRootState) => namedRequestData(state, deletePostTemplateInitiated.type))
  const dispatchClearRequests = useCallback(() => dispatch(clearRequests()), [dispatch])

  const handleDataFiltering = (page: number) => {
    setFilteredData([])
    if (filter.onlyFailedPostings) {
      dispatchGetDeferredPosts()
    } else {
      let topicIds: string[] = []
      // 1. alle abonnierbaren / alle Templates unterscheiden
      storedCategories?.topics?.map((category: TopicWithMetadata) => {
        if (category.topic.active) {
          if (category.topic.automaticPostingAllowed || category.topic.manualPostingAllowed) {
            if (!showTemplates) {
              category.topic.id && topicIds.push(category.topic.id)
            }
          } else {
            if (showTemplates) {
              category.topic.id && topicIds.push(category.topic.id)
            }
          }
        }
      })
      // 2. gesetzte Topic auswählen
      if (showTemplates) {
        if (selectedCategory) {
          topicIds = topicIds.filter(id => id === selectedCategory)
        }
      } else {
        if (selectedTopic) {
          topicIds = topicIds.filter(id => id === selectedTopic)
        }
      }

      // 3. Tags setzen
      const tagsIds = filter.tags.map(tag => tag.key)
      const params: FindPostTemplatesForCurrentUserLightweightRequest = {
        topicIds: topicIds && topicIds.length !== 0 ? topicIds : undefined,
        tagIds: tagsIds && tagsIds.length !== 0 ? tagsIds : undefined,
        sort: filter.sort,
        postStartTo: filter.postStartTo,
        limit: filter.limit,
        offset: pageSize * (page - 1),
        order: SortingOrder.Desc,
        includePostStartNull: true,
        includePostEndNull: true,
        excludePublishedTemplates: !showTemplates && user?.role === 'USER'
      }

      dispatchGetPostTemplates(params)
      dispatchClearRequests()
    }
  }

  const sendDeleteRequest = (deletePublished: boolean, templateId: string) => {
    setToast(null)
    dispatchDeletePostTemplate({ templateId, deletePublished })
  }

  const deletePostTemplateQuestion = (templateId: string) => {
    setToast({
      autoClose: false,
      open: true,
      type: 'error',
      title: t('networkContentMessages:deleteWarningTitle'),
      message: t('coreCalendar:deleteTemplateWarning'),
      severity: 'error',
      requestActions: true,
      confirmLabel: t('coreCalendar:templateAndPosts'),
      cancelLabel: t('coreCalendar:onlyTemplate'),
      handleClose: () => setToast(null),
      handleCancel: () => sendDeleteRequest(false, templateId),
      handleConfirm: () => sendDeleteRequest(true, templateId),
    })
  }

  const handleDeletePost = () => {
    handleDataFiltering(page)
  }

  const mapDeferredPostsContent = (deferredPosts: DeferredPost[], templates: PostTemplate[]): DeferredPost[] =>
    deferredPosts.map(post => {
      const template = templates.find(t => t.id === post.templateId)
      return template ? { ...post, overridingPostContent: { ...template.postContent } } : post
    })

  const handleUserShowFilteredData = () => {
    const templates = templateData?.templates || []
    const deferredPosts = templateData?.deferredPosts || []

    const filteredDeferredPosts = deferredPosts
      .filter(post => post.status !== DeferredPostStatus.ErrorWhilePosting)
      .map(post => mapDeferredPostsContent([post], templates)[0])

    const filteredTemplates = templates.filter(template =>
      !deferredPosts.some(post => template.id === post.templateId)
    )

    const filteredMultiData = [
      ...filteredTemplates,
      ...filteredDeferredPosts,
    ].sort((a, b) => (b.postStart?.getTime() || 0) - (a.postStart?.getTime() || 0))

    setFilteredData(filteredMultiData)
  }

  useEffect(() => {
    actionOnTemplateDelete?.status === 'success' && handleDeletePost()
  }, [actionOnTemplateDelete])

  useEffect(() => {
    if (templateData && user) {
      user?.role === 'USER' ? handleUserShowFilteredData() : setFilteredData(templateData.templates)
    }
  }, [templateData, user])

  useEffect(() => {
    const data: DropDownElement[] | undefined = showTemplates ? (categories || []) : (topics || [])
    setFilterOptions(initialFilterOptions)
    const topicData: DropDownElement[] = []
    const categoryData: DropDownElement[] = []

    if (data.length === 0) {
      templateData && storedCategories && storedCategories.topics.map(item => {
        if (templateData?.filteredTopicIds.findIndex((elem) => elem === item.topic.id) !== -1) {
          if (item.topic.id && item.topic.active) {
            if (item.topic.manualPostingAllowed || item.topic.automaticPostingAllowed) {
              item.topic.id && topicData.push({
                key: item.topic.id,
                value: item.topic.name,
              })
            } else {
              item.topic.id && categoryData.push({
                key: item.topic.id,
                value: item.topic.name,
              })
            }
          }
        }
      })

      setTopics(topicData)
      setCategories(categoryData)
    }
  }, [storedCategories, templateData])

  useEffect(() => {
    const data: DropDownElement[] | undefined = []
    templateData && tagsData && tagsData.map(tag => {
      if (templateData?.filteredTagIds.findIndex((elem) => elem === tag.id) !== -1) {
        if (tag.id) data.push({ key: tag.id, value: tag.label })
      }
    })
    setTags(data)
  }, [tagsData, templateData])

  useEffect(() => {
    handleDataFiltering(1)
  }, [filter, selectedTopic, selectedCategory, showTemplates, storedCategories])

  useEffect(() => {
    handleDataFiltering(page)
  }, [page])

  useEffect(() => {
    dispatch(setPostingType(showTemplates ? PostingType.template : PostingType.posting))
  }, [showTemplates])

  const isDeferredPostType = (posting: DeferredPost | PostTemplate) => {
    return !!(posting as DeferredPost).templateId
  }

  return (
    <>
      <ButtonComponent
        variant='text'
        color='info'
        className={classes.button}
        startIcon={<KeyboardArrowLeftRounded/>}
        text={t('postingCommon:backToPosting')}
        onClick={() => props.changePostingScreen && props.changePostingScreen(PostingScreen.MAIN)}
      />
      <Grid container>
        <Grid item xs={12}>
          <Paper className={classes.paper}>
            <Grid container spacing={2}>
              {((categories && categories.length > 0) || (topics && topics.length > 0)) &&
                <>
                  {filterOptions.length > 0 &&
                    <Grid container item xs={12} spacing={2}>
                      <Grid item xs={6}>
                        <DropDownComponent
                          options={filterOptions}
                          value={showTemplates ? FilterType.template : FilterType.topic}
                          label={t('postingCommon:postingType')}
                          size='small'
                          disableClearable
                          onChange={(option) => {
                            if (option.key === FilterType.template) {
                              setShowTemplates(true)
                              setSelectedCategory(undefined)
                              setSelectedTopic(undefined)
                            } else {
                              setShowTemplates(false)
                              setSelectedCategory(undefined)
                              setSelectedTopic(undefined)
                            }
                          }}
                        />
                      </Grid>
                    </Grid>}
                  <Grid item xs={6} lg={3}>
                    {showTemplates ? (
                      <DropDownComponent
                        disabled={filter.onlyFailedPostings}
                        options={categories}
                        label={t('postingCommon:category')}
                        size='small'
                        disableClearable={false}
                        placeholder={t('postingCommon:choose')}
                        value={selectedCategory}
                        onChange={(option) => {
                          if (!Array.isArray(option) && option !== null) {
                            setSelectedCategory(option.key)
                          } else {
                            setSelectedCategory(undefined)
                          }
                        }}
                      />)
                      : (
                        <DropDownComponent
                          disabled={filter.onlyFailedPostings}
                          options={topics}
                          label={t('postingCommon:topics')}
                          size='small'
                          disableClearable={false}
                          placeholder={t('postingCommon:choose')}
                          value={selectedTopic}
                          onChange={(option) => {
                            if (!Array.isArray(option) && option !== null) {
                              setSelectedTopic(option.key)
                            } else {
                              setSelectedTopic(undefined)
                            }
                          }}
                        />
                      )}
                  </Grid>
                </>}
              <Grid item xs={6} lg={3}>
                <DropDownComponent
                  disabled={filter.onlyFailedPostings}
                  options={sortingOptions}
                  label={t('postingCommon:sortingOption')}
                  size='small'
                  value={filter.sort}
                  onChange={(option) => {
                    if (!Array.isArray(option) && option !== null) {
                      let elem: PostTemplateSort = PostTemplateSort.ByPostStart
                      if (option.key === PostTemplateSort.ByPopularity) {
                        elem = PostTemplateSort.ByPopularity
                      }
                      setFilter({
                        ...filter,
                        sort: elem,
                      })
                    }
                  }}
                />
              </Grid>
              {tags && tags.length > 0 && (
                <Grid item xs={6}>
                  <DropDownComponent
                    disabled={filter.onlyFailedPostings}
                    multiple
                    limitTags={2}
                    size='small'
                    options={tags}
                    label={t('postingCommon:filterByTags')}
                    onChange={(options) => {
                      if (Array.isArray(options)) {
                        setFilter({
                          ...filter,
                          tags: options,
                        })
                      }
                    }}
                  />
                </Grid>
              )}
              {user?.role === 'USER' && !showTemplates &&
                <Grid item xs={12}>
                  <FormControlLabel
                    control={
                      <SwitchComponent
                        checked={filter.onlyFailedPostings}
                        color='primary'
                        onChange={e => {
                          setFilter({
                            ...filter,
                            onlyFailedPostings: e.target.checked,
                          })
                        }}
                      />
                    }
                    label={`${t('postingCommon:onlyFailedPostings')}`}
                    labelPlacement='end'
                  />
                </Grid>}
            </Grid>
          </Paper>
        </Grid>
        <Grid item xs={12}>
          {!filter.onlyFailedPostings && (
            <>
              {!!filteredData && !!filteredData.length
                ? filteredData.map((posting: PostTemplate | DeferredPost, index) =>
                  <ShowPostTemplate
                    key={index}
                    user={user}
                    filterType={showTemplates ? FilterType.template : FilterType.topic}
                    templateData={templateData}
                    posting={isDeferredPostType(posting) ? undefined : posting as PostTemplate}
                    deferredPosting={isDeferredPostType(posting) ? posting as DeferredPost : undefined}
                    changePosting={props.changePosting}
                    changePostingScreen={props.changePostingScreen}
                    deletePosting={deletePostTemplateQuestion}
                  />) : actionGetPostTemplates?.status === 'success'
                  ? <NoDataComponent noDataText={t('postingCommon:noPostingData')} alignment="center" margin="16px 0"/>
                  : <Preloader/>}
            </>
          )}

          {filter.onlyFailedPostings && (
            <>
              {!!deferredPostsData && !!deferredPostsData.deferredPosts.length
                ? deferredPostsData.deferredPosts
                  .filter((posting: DeferredPost) => posting.status === DeferredPostStatus.ErrorWhilePosting)
                  .map((posting: DeferredPost, index) =>
                    <ShowPostTemplate
                      key={index}
                      user={user}
                      templateData={templateData}
                      posting={undefined}
                      deferredPosting={posting}
                      changePosting={props.changePosting}
                      changePostingScreen={props.changePostingScreen}
                      deletePosting={deletePostTemplateQuestion}
                    />
                  ) : actionGetDeferredPosts?.status === 'success'
                  ? <NoDataComponent noDataText={t('postingCommon:noPostingData')} alignment="center" margin="16px 0"/>
                  : <Preloader/>}
            </>
          )}
        </Grid>
        <Grid item xs={12}>
          <Box component='div' style={{ display: 'flex', justifyContent: 'center' }}>
            {templateData && !filter.onlyFailedPostings &&
              <Pagination
                totalItems={templateData.total}
                itemsPerPage={filter.limit}
                page={page}
                setPage={setPage}
              />}
          </Box>
        </Grid>
        {toast &&
          <DialogComponent
            type={toast.type || undefined}
            autoClose={toast.autoClose}
            title={toast.title}
            message={toast.message}
            open={toast.open}
            confirmLabel={toast.confirmLabel}
            cancelLabel={toast.cancelLabel}
            requestActions={toast.requestActions}
            onClose={toast.handleClose}
            onCancel={toast.handleCancel}
            onConfirm={toast.handleConfirm}
            closeIcon={true}
          />}
      </Grid>
    </>
  )
}

export default Template
